import { useNavigate } from "@remix-run/react";
import {
  WS_EVENTS,
  type NotificationMessage,
  type NotificationType,
  type AchievementAwardedMessage,
  BADGE_TYPE,
} from "@repo/shared";
import { TROPHY_TYPE } from "@repo/shared";
import { cva } from "class-variance-authority";
import { toPng } from "html-to-image";
import { camelCase } from "lodash-es";
import Lottie from "lottie-react";
import { useEffect, useId, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast as gamificationToast } from "sonner";
import { match, P } from "ts-pattern";

import { getBadgeImagePath } from "~/assets/badges";
import VERIFICATION_PHONE from "~/assets/verification-phone.png";
import { Icon } from "~/components/Icon";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { useToast } from "~/components/ui/use-toast";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { SearchBadge } from "~/modules/Client/Courses/SearchLesson/SearchBadge";
import { BadgeGraphic } from "~/modules/Client/Gamification/components/BadgeGraphic";
import { GamificationToast } from "~/modules/Client/Gamification/components/GamificationToast";
import { getBadgeVariant } from "~/utils/getBadgeVariant";

import tadaAnimation from "../assets/animations/tada.json";
import { useWebSocket } from "../hooks/useWebSocket";

import type { ToastVariants } from "./ui/toast";
import type { BadgeGraphicBadge } from "~/modules/Client/Gamification/components/BadgeGraphic";

const popoverBadgeVariants = cva(
  "details flex w-max gap-x-1 items-center justify-between px-2 py-[1.5px] border rounded-lg",
  {
    variants: {
      variant: {
        1: "gamification__badge-1",
        2: "gamification__badge-2",
        3: "gamification__badge-3",
        4: "gamification__badge-4",
        [TROPHY_TYPE.STARTER]: "gamification__badge-starter",
      },
    },
    defaultVariants: {
      variant: 1,
    },
  },
);

export const NotificationListener = () => {
  const { subscribe } = useWebSocket();
  const { toast } = useToast();
  const [achievement, setAchievement] = useState<AchievementAwardedMessage | null>(null);
  const [dialogAchievement, setDialogAchievement] = useState<AchievementAwardedMessage | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeviceVerifiedDialogOpen, setIsDeviceVerifiedDialogOpen] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const { t } = useTranslation();
  const badgeDialogTitleId = useId();
  const deviceDialogTitleId = useId();
  const navigate = useNavigate();
  const badge = dialogAchievement?.badge;

  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isMobile = useMediaQuery({ maxWidth: 1023 });
  const variant = badge && 1 <= badge.tier && badge.tier <= 4 ? (badge.tier as 1 | 2 | 3 | 4) : 1;
  const badgeImage = badge ? getBadgeImagePath(badge.badgeAvatarId) : undefined;
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const handleShareClick = () => setShowShareOptions((v) => !v);

  const handleNavigateAndClose = () => {
    setIsDialogOpen(false);
    setAchievement(null);
    setDialogAchievement(null);
    setShowShareOptions(false);
    navigate("/user-progress");
  };

  const handleDeviceVerifiedContinue = () => {
    setIsDeviceVerifiedDialogOpen(false);
    window.location.href = "/";
  };

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (dialogContentRef.current) {
      try {
        const dataUrl = await toPng(dialogContentRef.current, { cacheBust: true });
        const link = document.createElement("a");

        link.href = dataUrl;
        link.download = `${badge?.name || "badge"}.png`;

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
      } catch (error) {
        console.error("Failed to generate image", error);
      }
    } else {
      console.error("Dialog content ref is not set.");
    }
  };

  useEffect(() => {
    if (!subscribe) {
      return;
    }

    const unsubNotification = subscribe<NotificationMessage>(WS_EVENTS.NOTIFICATION, (data) => {
      toast({
        variant: mapNotificationType(data.type),
        description: data.message || "Otrzymano powiadomienie",
        duration: 5000,
      });
    });

    const unsubSystemNotification = subscribe<NotificationMessage>(
      WS_EVENTS.SYSTEM_NOTIFICATION,
      (data) => {
        toast({
          variant: mapNotificationType(data.type || "warning"),
          description: data.message || "Powiadomienie systemowe",
          duration: 5000,
        });
      },
    );

    const unsubAchievementAwarded = subscribe<AchievementAwardedMessage>(
      WS_EVENTS.ACHIEVEMENT_AWARDED,
      (data) => {
        if (data.badge) {
          if (data.badge.trophyType === TROPHY_TYPE.STARTER) {
            setIsDialogOpen(true);
          }

          if (data.badge.trophyType !== TROPHY_TYPE.STARTER) {
            setAchievement(data);
          }

          setDialogAchievement(data);
        }
      },
    );

    const unsubDeviceVerified = subscribe<NotificationMessage>(WS_EVENTS.DEVICE_VERIFIED, () => {
      setIsDeviceVerifiedDialogOpen(true);
    });

    return () => {
      if (unsubNotification) unsubNotification();
      if (unsubSystemNotification) unsubSystemNotification();
      if (unsubAchievementAwarded) unsubAchievementAwarded();
      if (unsubDeviceVerified) unsubDeviceVerified();
    };
  }, [subscribe, toast]);

  useEffect(() => {
    if (achievement) {
      const toastId = gamificationToast.custom(
        (id) => (
          <GamificationToast
            badge={achievement.badge}
            onViewBadge={() => {
              setIsDialogOpen(true);
              setDialogAchievement(achievement);
              setAchievement(null);
              gamificationToast.dismiss(toastId);
            }}
            onDismiss={() => {
              setAchievement(null);
              gamificationToast.dismiss(toastId);
            }}
            id={id.toString()}
          />
        ),
        {
          duration: 10000,
        },
      );
    }
  }, [achievement, toast]);

  return (
    <>
      <div className="fixed left-0 top-0 -z-[calc(infinity)] opacity-0" {...{ inert: "" }}>
        <div ref={dialogContentRef} className="flex w-full flex-col items-center">
          {dialogAchievement?.badge && (
            <BadgeGraphic badge={dialogAchievement.badge as BadgeGraphicBadge} />
          )}
        </div>
      </div>
      {isDialogOpen && (
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) setAchievement(null);
            if (!open) setDialogAchievement(null);
            if (!open) setShowShareOptions(false);
          }}
        >
          <DialogContent
            aria-labelledby={badgeDialogTitleId}
            className="min-h-[min(493px,100dvh)] w-[calc(100vw-2rem)] max-w-[358px] !rounded-3xl py-6 sm:min-h-[min(657px,100dvh)] sm:w-[560px] sm:max-w-[560px] sm:px-9 lg:p-[52px]"
          >
            <div className="relative flex flex-col items-center">
              <div className="absolute inset-0 -z-[1]">
                <Lottie animationData={tadaAnimation} loop={false} className="size-full" />
              </div>
              <div
                className={popoverBadgeVariants({
                  variant:
                    dialogAchievement?.badge.trophyType === TROPHY_TYPE.STARTER
                      ? TROPHY_TYPE.STARTER
                      : variant,
                })}
              >
                {dialogAchievement?.badge.trophyType === TROPHY_TYPE.STARTER ? (
                  <p>{t("userProgressPage.gamificationAchievements.starter.name")}</p>
                ) : (
                  <p className="font-bold">
                    {t(
                      `userProgressPage.gamificationAchievements.${camelCase(badge?.trophyType)}.name`,
                    )}
                    : {t("gamification.level", { level: variant })}
                  </p>
                )}
              </div>
              <img
                src={badgeImage}
                alt={badge?.name}
                className="mt-3 lg:mt-6"
                style={{
                  width: isDesktop ? "335px" : "286px",
                  height: isDesktop ? "240px" : "218px",
                  objectFit: "contain",
                }}
              />
              {dialogAchievement?.badge.schoolClassName &&
                dialogAchievement?.badge?.badgeType !== BADGE_TYPE.POWER_SIGN && (
                  <SearchBadge
                    variant={getBadgeVariant(dialogAchievement?.badge.schoolClassName)}
                    className="mt-2 2xl:mt-3"
                  />
                )}
              {/* TODO: add date from backend */}
              <hgroup className="mt-5 text-center lg:mt-10">
                <h2 id={badgeDialogTitleId} className="h5 lg:h4 text-violet-800">
                  {badge?.trophyType === TROPHY_TYPE.STARTER
                    ? t("gamification.goodStartTitle")
                    : t("gamification.congratulationsTitle")}
                </h2>
                <p className="body-sm sm:body-base mt-1 max-w-[334px] text-violet-800 sm:px-2 lg:max-w-[456px]">
                  {(badge?.description?.match(/[^.]+\.|[^.]+$/g) || []).map((part, index) => {
                    return part.trim().startsWith("*") ? (
                      <div key={index} className="body-sm">
                        {part}
                      </div>
                    ) : (
                      <span key={index}>{part}</span>
                    );
                  })}
                </p>
              </hgroup>
              <div className="mt-4 w-full max-w-[334px] lg:mt-8 lg:max-w-[456px]">
                {!showShareOptions ? (
                  <div className="flex gap-x-2">
                    <Button onClick={handleShareClick} className="w-full gap-x-2">
                      {t("gamification.shareAchievement")}
                      <Icon name="SHARE" className="size-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-max shrink-0 gap-x-2"
                      onClick={(e) => handleDownload(e)}
                    >
                      {t("userProgressPage.downloadBadge")}
                      <Icon name="DOWNLOADOUTLINED" className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-x-2">
                      <div className="inline-flex flex-1 items-center gap-x-2 overflow-hidden rounded-lg border border-neutral-300 bg-white px-3 py-2.5">
                        <input
                          type="text"
                          value={`${window.location.origin}/shared-achievement/${badge?.achievementBadgeId}`}
                          readOnly
                          className="details flex-1 bg-white text-neutral-600 focus:border-none focus:outline-none"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `${window.location.origin}/shared-achievement/${badge?.achievementBadgeId}`,
                            );
                            toast({
                              description: (
                                <span>
                                  <strong>{t("gamificationBadge.toast.linkCopied")}</strong>{" "}
                                  {t("gamificationBadge.toast.linkCopiedDescription")}
                                </span>
                              ),
                              variant: "message-success",
                            });
                          }}
                        >
                          <Icon name="COPY" className="size-5" />
                        </button>
                      </div>
                      {!isMobile && (
                        <Button
                          variant="secondary"
                          className="w-max shrink-0 gap-x-2"
                          onClick={(e) => handleDownload(e)}
                        >
                          {t("userProgressPage.downloadBadge")}
                          <Icon name="DOWNLOADOUTLINED" className="size-5" />
                        </Button>
                      )}
                    </div>
                    {showShareOptions && (
                      <div className="flex w-full justify-center">
                        <span className="details mt-2 font-bold">
                          {t("gamificationBadge.share.description")}
                        </span>
                      </div>
                    )}
                    {isMobile && (
                      <Button
                        variant="secondary"
                        className="w-full gap-x-2"
                        onClick={(e) => handleDownload(e)}
                      >
                        {t("userProgressPage.downloadBadge")}
                        <Icon name="DOWNLOADOUTLINED" className="size-4" />
                      </Button>
                    )}
                  </div>
                )}
                <Button
                  onClick={handleNavigateAndClose}
                  variant="link"
                  className="mt-2 w-full lg:mt-3"
                >
                  {t("gamification.viewAllAchievements")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {isDeviceVerifiedDialogOpen && (
        <Dialog open={isDeviceVerifiedDialogOpen} onOpenChange={() => {}}>
          <DialogContent
            aria-labelledby={deviceDialogTitleId}
            className="w-[calc(100vw-2rem)] max-w-[440px] !rounded-3xl py-8 text-center"
            hideCloseButton
          >
            <div className="flex flex-col items-center space-y-4">
              <img src={VERIFICATION_PHONE} alt="" className="h-[124px] w-[126px]" />
              <h2 id={deviceDialogTitleId} className="font-madimi text-2xl text-neutral-950">
                Twoje urządzenie zostało potwierdzone przez opiekuna!
              </h2>

              <Button
                onClick={handleDeviceVerifiedContinue}
                className="w-full font-sourceSans text-sm text-violet-950"
              >
                Kontynuuj naukę
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const mapNotificationType = (type?: NotificationType) => {
  return match(type)
    .returnType<Partial<ToastVariants["variant"]>>()
    .with("error", () => "destructive")
    .with("warning", () => "warning")
    .with("info", () => "info")
    .with(P.nullish, "success", () => "default")
    .exhaustive();
};
